import type React from "react";
import Layout from "@/components/Layout";
import { Hero } from "@/components/home/Hero";
import aboutImage from "@/assets/Roberto Pesce - Photo 2.jpg";

/* ── Data ───────────────────────────────────────────────── */

const testimonials = [
  {
    quote:
      "Roberto is one of the most driven and resourceful individuals I've ever met. He's got a great mind. If you have the chance to work with him you'll have a powerful team member.",
    author: "Kris Schlesser",
    role: "Founder",
    company: "LuckyBolt",
    url: "https://luckybolt.com/",
  },
  {
    quote:
      "Roberto easily handled everything from publishing a new product version, communicating with stakeholders and partners, to writing down and executing the marketing strategy.",
    author: "Pavel Karateev",
    role: "PyCharm Team Lead",
    company: "JetBrains",
    url: "https://jetbrains.com/",
  },
  {
    quote:
      "Roberto led product with focus and clear goals, allowing the team to deliver value promptly. He is respectful and a great communicator. It's always a pleasure to work with him.",
    author: "Juozapas Zabuskas",
    role: "CTO",
    company: "Convious",
    url: "https://convious.com/",
  },
];

/* ── Shared styles ──────────────────────────────────────── */

const sectionBase: React.CSSProperties = { position: "relative", zIndex: 1 };

const gridBorder = (cols: string): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: cols,
  gap: "1.5px",
  background: "var(--border-gold)",
  border: "1px solid var(--border-gold)",
  marginTop: "4rem",
});

const bodyP: React.CSSProperties = {
  fontSize: "0.95rem",
  color: "var(--dp-muted)",
  lineHeight: 1.9,
  marginBottom: "1.3rem",
  maxWidth: "54ch",
};

/* ── Component ──────────────────────────────────────────── */

const Index = () => (
  <Layout>
    {/* ── Hero ──────────────────────────────────────────── */}
    <Hero />

    {/* ── Logos bar ─────────────────────────────────────── */}
    <div
      style={{
        background: "var(--navy-2)",
        borderTop: "1px solid var(--border-gold)",
        borderBottom: "1px solid var(--border-gold)",
        position: "relative",
        zIndex: 1,
        padding: "1.8rem 5vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "3.5rem",
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: "0.68rem",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "var(--dp-muted)",
          whiteSpace: "nowrap",
        }}
      >
        Worked with
      </span>
      {["JetBrains", "Convious", "Ambev", "Carrier", "LuckyBolt", "Brazuka Tickets"].map(
        (name) => (
          <span
            key={name}
            style={{
              fontSize: "0.82rem",
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(122,134,153,0.5)",
              transition: "color 0.2s",
              cursor: "default",
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--dp-muted)")}
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLElement).style.color = "rgba(122,134,153,0.5)")
            }
          >
            {name}
          </span>
        )
      )}
    </div>

    {/* ── How I Work ────────────────────────────────────── */}
    <section id="belief" style={{ ...sectionBase, background: "var(--navy-2)" }}>
      <div className="section-inner">
        <div style={{ maxWidth: 820 }}>
          <div className="section-label">How I work</div>
          <h2 className="section-title">
            The information
            <br />
            is almost always
            <br />
            <em>already there</em>
          </h2>

          <div style={{ marginTop: "3rem" }}>
            <p
              style={{
                fontSize: "1.08rem",
                color: "var(--dp-muted)",
                lineHeight: 1.95,
                marginBottom: "1.5rem",
                maxWidth: "68ch",
              }}
            >
              The pattern is the same every time: the answer to a market problem is already in the
              customer conversations, the sales calls, the support tickets. I find it. Then I build
              the positioning, the messaging, and the GTM motion around what the data is actually
              saying.
            </p>

            <div
              style={{
                borderLeft: "2px solid rgba(201,169,110,0.4)",
                background: "var(--navy-3)",
                padding: "1.6rem 1.8rem",
                maxWidth: "68ch",
              }}
            >
              <div
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "var(--gold)",
                  marginBottom: "1.2rem",
                }}
              >
                Some examples
              </div>

              <p
                style={{
                  fontSize: "1.08rem",
                  color: "var(--dp-muted)",
                  lineHeight: 1.95,
                  marginBottom: "0.9rem",
                }}
              >
                At Convious, the airline analogy in sales calls became{" "}
                <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                  Real-Time Pricing, the centerpiece of a $21M Series A.
                </strong>
              </p>

              <p
                style={{
                  fontSize: "1.08rem",
                  color: "var(--dp-muted)",
                  lineHeight: 1.95,
                  marginBottom: "0.9rem",
                }}
              >
                At JetBrains, "I open PyCharm when I need to get something done" became{" "}
                <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                  "PyCharm. Get It Done."
                </strong>{" "}
                It launched at PyCon US.
              </p>

              <p
                style={{
                  fontSize: "1.08rem",
                  color: "var(--dp-muted)",
                  lineHeight: 1.95,
                  marginBottom: 0,
                }}
              >
                At every advisory engagement since, the same patterns repeated: messaging drift,
                positioning decisions made on instinct. That observation became{" "}
                <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                  ClarityPie.
                </strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Stories ───────────────────────────────────────── */}
    <section id="stories" style={{ ...sectionBase, background: "var(--navy)" }}>
      <div className="section-inner">
        <div style={{ maxWidth: 600, marginBottom: "5rem" }}>
          <div className="section-label">Work</div>
          <h2 className="section-title">
            Two stories that
            <br />
            <em>explain how I think</em>
          </h2>
          <p className="section-sub">
            Not a timeline. Two moments where the real opportunity wasn't the obvious one, and what
            happened when we went after it.
          </p>
        </div>

        {/* Story 1: Convious */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1.5px",
            background: "var(--border-gold)",
            border: "1px solid var(--border-gold)",
            marginBottom: "1.5px",
          }}
          className="story-grid"
        >
          <div
            style={{
              background: "var(--navy-2)",
              padding: "3.5rem 3rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "var(--gold)",
                  marginBottom: "0.6rem",
                }}
              >
                Convious · Amsterdam · 2018–2020
              </div>
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)",
                  fontWeight: 300,
                  lineHeight: 1.2,
                  marginBottom: "2rem",
                  color: "var(--dp-white)",
                }}
              >
                From a product
                <br />
                nobody trusted
                <br />
                to a <em style={{ fontStyle: "italic", color: "var(--gold-lt)" }}>$21M thesis</em>
              </h3>
            </div>
            <div>
              <span
                style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "var(--dp-muted)",
                  marginBottom: "0.8rem",
                  display: "block",
                }}
              >
                What came of it
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {[
                  { num: "$21M", label: "Series A, anchored by the RTP engine" },
                  { num: "3×", label: "Revenue growth during tenure" },
                  { num: "75%", label: "Reduction in customer onboarding time" },
                ].map((stat) => (
                  <div
                    key={stat.num}
                    style={{ display: "flex", alignItems: "baseline", gap: "0.6rem" }}
                  >
                    <span
                      style={{
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        fontSize: "2rem",
                        fontWeight: 300,
                        color: "var(--gold-lt)",
                        lineHeight: 1,
                      }}
                    >
                      {stat.num}
                    </span>
                    <span
                      style={{ fontSize: "0.82rem", color: "var(--dp-muted)", lineHeight: 1.4 }}
                    >
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: "var(--navy)", padding: "3.5rem 3rem" }}>
            <p style={bodyP}>
              When I joined Convious, the company was selling a "Name Your Own Price" feature to
              amusement parks and museums. The product made sense on paper: let demand drive price.
              In practice, it was almost impossible to sell. Leisure operators didn't want to hand
              their pricing strategy to visitors. It sounded like a gimmick. It sounded like they'd
              lose control of their most important seasonal revenue.
            </p>
            <p style={bodyP}>
              The co-founder and I identified the opportunity together after sitting through enough
              sales calls to notice something: every time the salesperson explained dynamic pricing
              using airlines as the analogy, the room relaxed. Everyone already understood that
              model. Nobody felt cheated by it.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                The concept wasn't foreign. The framing was wrong.
              </strong>
            </p>
            <p style={bodyP}>
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                So I took the PM role and built the thing from scratch.
              </strong>{" "}
              I interviewed customers about their pricing fears, their seasonal pressures, the
              parameters they needed to feel in control. I wrote the functional specs and developed
              the pricing algorithm with the data science team: a real-time engine accounting for
              demand, seasonality, holidays, and weather. Then I designed the customer-facing pricing
              UI. The concept was clear: show prices the way airline booking sites do, using the
              visual language the market already trusted. The execution had decisions worth making.
              We tested week view versus full month, whether to show dollar signs or not, and the
              meaning of red. Red was the hardest call. It could read as the day being full rather
              than expensive. We tested it. In context, red reliably communicated higher price
              without being read as sold out.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                Green, yellow, red.
              </strong>{" "}
              Familiar. Trustworthy. The UI
              reduced sales-call friction immediately. Operators recognized the interface before we
              finished explaining it.
            </p>
            <p style={bodyP}>
              Before launch, I built the full commercial system:{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                positioning document, messaging angles, sales pitches, battle cards, one-pagers,
                landing page.
              </strong>{" "}
              I ran monthly sessions with the sales team and listened to calls via HubSpot to keep
              refining. The feature was dramatically easier to sell than what came before. It became
              the company's central commercial bet and the core of the{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                $21M Series A
              </strong>{" "}
              pitch.
            </p>
            <p style={bodyP}>
              In parallel, a self-serve migration I led cut customer onboarding time from{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                two weeks to three days.
              </strong>{" "}
              During my two years,{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                revenue tripled
              </strong>{" "}
              and the partner base grew at the same pace.
            </p>
            <p style={{ ...bodyP, marginBottom: 0 }}>
              By the time I left, Real-Time Pricing had evolved into a full pricing platform
              supporting multiple strategies, with a machine-learning layer, customer-facing
              controls, and a standalone API.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                The product I helped build was the company.
              </strong>
            </p>
          </div>
        </div>

        {/* Story 2: JetBrains */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1.5px",
            background: "var(--border-gold)",
            border: "1px solid var(--border-gold)",
          }}
          className="story-grid"
        >
          <div
            style={{
              background: "var(--navy-2)",
              padding: "3.5rem 3rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "var(--gold)",
                  marginBottom: "0.6rem",
                }}
              >
                JetBrains · Amsterdam · 2020–2024
              </div>
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)",
                  fontWeight: 300,
                  lineHeight: 1.2,
                  marginBottom: "2rem",
                  color: "var(--dp-white)",
                }}
              >
                Don't fight
                <br />
                the war you
                <br />
                <em style={{ fontStyle: "italic", color: "var(--gold-lt)" }}>cannot win</em>
              </h3>
            </div>
            <div>
              <span
                style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "var(--dp-muted)",
                  marginBottom: "0.8rem",
                  display: "block",
                }}
              >
                What came of it
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {[
                  { num: "98%", label: "Revenue growth over four years" },
                  { num: "2×", label: "User base, paid and free combined" },
                  { num: "50%", label: "Lift in Pro trial conversion" },
                ].map((stat) => (
                  <div
                    key={stat.num}
                    style={{ display: "flex", alignItems: "baseline", gap: "0.6rem" }}
                  >
                    <span
                      style={{
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        fontSize: "2rem",
                        fontWeight: 300,
                        color: "var(--gold-lt)",
                        lineHeight: 1,
                      }}
                    >
                      {stat.num}
                    </span>
                    <span
                      style={{ fontSize: "0.82rem", color: "var(--dp-muted)", lineHeight: 1.4 }}
                    >
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: "var(--navy)", padding: "3.5rem 3rem" }}>
            <p style={bodyP}>
              I was hired to help fight VS Code. Within a few months of research, I concluded that
              was the wrong fight and said so.
            </p>
            <p style={bodyP}>
              VS Code wasn't an IDE competing with PyCharm. It was a free acquisition channel for
              Microsoft Azure, subsidised by one of the richest companies in the world. Trying to
              out-compete that on features or price was unwinnable.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                The right question wasn't "how do we beat VS Code?" It was "where can we win that
                VS Code isn't trying to win?"
              </strong>
            </p>
            <p style={bodyP}>
              The research pointed somewhere unexpected. I reviewed every quantitative study
              available, including the Python Developer Survey, ran dozens of user interviews,
              conducted diary studies asking PyCharm and VS Code users to switch tools, and scraped
              review platforms and developer communities for unfiltered signal. What emerged was
              clear: there was a large and growing segment of professional Python users (data
              scientists, analysts, ML practitioners) who had outgrown Jupyter notebooks, didn't
              identify as traditional developers, and haven't commited with any serious tool yet.
              PyCharm's brand was trusted, often more recognisable than JetBrains itself. The
              opportunity was to extend that trust into this segment before anyone else did.
            </p>
            <p style={bodyP}>
              The internal argument was harder than the strategy. The company was planning a separate
              Data Science IDE. I argued against it. Splitting the brand would dilute the very
              strength we were trying to leverage. Every feature built for the new IDE should go into
              PyCharm. The positioning should expand PyCharm's identity, not fragment it.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                That argument required escalation. It took time. It won.
              </strong>
            </p>
            <p style={bodyP}>
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                "PyCharm. Get It Done."
              </strong>{" "}
              launched at PyCon US 2023, the largest Python conference in the world.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                The tagline came directly from user interviews:
              </strong>{" "}
              people said they used VS Code for quick edits, but
              opened PyCharm when they really needed to get something done. We built the campaign
              around the language our users already used to describe us. We invested in data science
              communities, partnered with NumFOCUS and the Python Software Foundation, hired and
              directed a dedicated data science developer advocate, and rebuilt the onboarding and
              nurture flows for the segment.
            </p>
<p style={bodyP}>
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                The repositioning doubled the user base and drove 98% revenue growth over four years
                on a platform that had already been live for a decade.
              </strong>
            </p>
            <p style={{ ...bodyP, marginBottom: 0 }}>
              At that point, PyCharm was no longer a web development IDE. It was 50% web, 50%
              data science.{" "}
              <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                A complete category repositioning, not a campaign.
              </strong>
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ── Testimonials ──────────────────────────────────── */}
    <section id="testimonials" style={{ ...sectionBase, background: "var(--navy-2)" }}>
      <div className="section-inner">
        <div className="section-label">Testimonials</div>
        <h2 className="section-title">
          What colleagues
          <br />
          <em>say</em>
        </h2>

        <div style={gridBorder("repeat(3, 1fr)")} className="testimonials-responsive">
          {testimonials.map((t) => (
            <div
              key={t.author}
              style={{
                background: "var(--navy-2)",
                padding: "2.6rem 2.4rem",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "var(--navy-3)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "var(--navy-2)")
              }
            >
              <div
                style={{
                  color: "var(--gold)",
                  fontSize: "0.8rem",
                  marginBottom: "1.3rem",
                }}
              >
                ★★★★★
              </div>
              <p
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "1.08rem",
                  fontWeight: 300,
                  fontStyle: "italic",
                  color: "var(--dp-white)",
                  lineHeight: 1.7,
                  marginBottom: "1.8rem",
                }}
              >
                "{t.quote}"
              </p>
              <div>
                <strong
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "var(--dp-white)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {t.author}
                </strong>
                <span style={{ fontSize: "0.78rem", color: "var(--dp-muted)" }}>
                  {t.role} @{" "}
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--gold)", textDecoration: "none" }}
                  >
                    {t.company}
                  </a>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── About ─────────────────────────────────────────── */}
    <section id="about" style={{ ...sectionBase, background: "var(--navy-2)", borderTop: "1px solid var(--border-gold)" }}>
      <div className="section-inner">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "320px 1fr",
            gap: "6rem",
            alignItems: "start",
          }}
          className="about-grid"
        >
          <div>
            <div style={{ position: "relative" }}>
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: -14,
                  right: -14,
                  width: "50%",
                  height: "50%",
                  borderTop: "1px solid rgba(198,169,106,0.35)",
                  borderRight: "1px solid rgba(198,169,106,0.35)",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  bottom: -14,
                  left: -14,
                  width: "32%",
                  height: "32%",
                  borderBottom: "1px solid rgba(198,169,106,0.2)",
                  borderLeft: "1px solid rgba(198,169,106,0.2)",
                  pointerEvents: "none",
                }}
              />
              <img
                src={aboutImage}
                alt="Roberto Pesce"
                style={{
                  width: "100%",
                  display: "block",
                  borderRadius: 2,
                  filter: "grayscale(15%) contrast(1.04)",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to top, rgba(13,20,32,0.4) 0%, transparent 50%)",
                  borderRadius: 2,
                }}
              />
            </div>
          </div>

          <div>
            <div className="section-label">About</div>
            <h2 className="section-title" style={{ marginBottom: "2rem" }}>
              A little
              <br />
              <em>about me</em>
            </h2>

            <p
              style={{
                color: "var(--dp-muted)",
                fontSize: "0.96rem",
                lineHeight: 1.9,
                marginBottom: "1.3rem",
                maxWidth: "64ch",
              }}
            >
              I grew up in Brazil, started my career in marketing and sales at Carrier and AmBev,
              and spent my mid-twenties in San Diego advising early-stage startups on product and GTM
              while studying graphic design. I moved to Amsterdam in 2018 to join Convious as their
              first growth hire and never really left.
            </p>
            <p
              style={{
                color: "var(--dp-muted)",
                fontSize: "0.96rem",
                lineHeight: 1.9,
                marginBottom: "1.3rem",
                maxWidth: "64ch",
              }}
            >
              I've worked across every stage of company: two-person startups, Series A bets, and
              global products with millions of users. Along the way I've sat in growth, product, and
              product marketing chairs, sometimes all at once. That range sharpens the product
              marketing work. The best PMMs understand what happens on both sides of the
              product-market boundary because they've been there.
            </p>
            <p
              style={{
                color: "var(--dp-muted)",
                fontSize: "0.96rem",
                lineHeight: 1.9,
                marginBottom: "1.3rem",
                maxWidth: "64ch",
              }}
            >
              Outside of work I'm building ClarityPie, teaching myself to ship with AI tools, and
              spending time with my wife and two kids in Amsterdam. The building and the parenting
              teach the same skill: patience with things that take longer than expected.
            </p>
            <p
              style={{
                color: "var(--dp-muted)",
                fontSize: "0.96rem",
                lineHeight: 1.9,
                maxWidth: "64ch",
              }}
            >
              I'm currently looking for my next senior PMM role at a company where product marketing
              is treated as a commercial function, not a support one.
            </p>

            <div
              style={{
                marginTop: "2.5rem",
                paddingTop: "2rem",
                borderTop: "1px solid rgba(245,243,239,0.07)",
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem 1.5rem",
              }}
            >
              {[
                { label: "Languages", detail: "English, Portuguese, Spanish" },
                { label: "Based", detail: "Amsterdam, NL" },
              ].map((item) => (
                <span
                  key={item.label}
                  style={{
                    fontSize: "0.78rem",
                    color: "var(--dp-muted)",
                    letterSpacing: "0.04em",
                  }}
                >
                  <strong style={{ color: "var(--dp-muted)", fontWeight: 400 }}>
                    {item.label}
                  </strong>{" "}
                  · {item.detail}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Building in Public ────────────────────────────── */}
    <section id="building" style={{ ...sectionBase, background: "var(--navy)" }}>
      <div className="section-inner">
        <div className="section-label">Building in Public</div>
        <h2 className="section-title">
          What I'm working
          <br />
          on <em>outside of work</em>
        </h2>

        <div style={gridBorder("1fr")}>
          <div
            style={{
              background: "var(--navy)",
              padding: "3rem",
              display: "flex",
              flexDirection: "column",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLElement).style.background = "var(--navy-3)")
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLElement).style.background = "var(--navy)")
            }
          >
            <div
              style={{
                fontSize: "0.68rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "var(--gold)",
                marginBottom: "1.4rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <span
                style={{
                  display: "block",
                  width: "1rem",
                  height: "1px",
                  background: "var(--gold)",
                  flexShrink: 0,
                }}
              />
              B2B SaaS · Side Project
            </div>
            <h3
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: "1.8rem",
                fontWeight: 300,
                marginBottom: "1.5rem",
                lineHeight: 1.2,
                color: "var(--dp-white)",
              }}
            >
              ClarityPie
            </h3>
            <div
              style={{
                color: "var(--dp-muted)",
                fontSize: "0.9rem",
                lineHeight: 1.8,
                marginBottom: "2rem",
                flex: 1,
                maxWidth: "72ch",
              }}
            >
              <p style={{ marginBottom: "1.2rem" }}>
                An AI-powered positioning system for post-PMF B2B SaaS teams. Built end-to-end as
                sole designer and developer:{" "}
                <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                  React frontend, Supabase backend, n8n orchestration, and LLM pipelines hosted on
                  Hetzner.
                </strong>
              </p>
              <p style={{ marginBottom: "1.2rem" }}>
                The thesis: positioning decisions get made on instinct, internal politics, and
                outdated assumptions rather than real market signal. I kept seeing the same three
                problems repeat across every company I worked with:{" "}
                <strong style={{ color: "var(--dp-white)", fontWeight: 400 }}>
                  messaging drift, internal misalignment, and positioning decisions made without
                  traceability.
                </strong> ClarityPie is the
                system I built to fix that.
              </p>
              <p style={{ marginBottom: "1.2rem" }}>
                Users upload assets, share URLs, and send structured discovery surveys to their
                team. The system extracts positioning signals from each source, synthesizes them
                into a pre-generated positioning draft across ICP, differentiation, proof claims,
                and competitive angle, and tracks every signal to its line-level source. A
                PMM-driven builder then guides the team through open questions, conflict flagging,
                and multi-user approval, producing a versioned, timestamped positioning strategy.
              </p>
              <p>Pre-launch, 100+ users on the waitlist.</p>
            </div>
            <a
              href="https://claritypie.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                color: "var(--gold)",
                textDecoration: "none",
                fontSize: "0.78rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                borderBottom: "1px solid var(--border-gold)",
                paddingBottom: "0.3rem",
                transition: "border-color 0.2s, gap 0.2s",
                alignSelf: "flex-start",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "var(--gold)";
                (e.currentTarget as HTMLElement).style.gap = "0.8rem";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "var(--border-gold)";
                (e.currentTarget as HTMLElement).style.gap = "0.5rem";
              }}
            >
              Visit ClarityPie
              <svg
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                style={{ width: 12, height: 12 }}
              >
                <path d="M2 10L10 2M4 2h6v6" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ── Contact ───────────────────────────────────────── */}
    <section
      id="contact"
      style={{
        ...sectionBase,
        background: "var(--navy-2)",
        borderTop: "1px solid var(--border-gold)",
      }}
    >
      <div
        style={{
          maxWidth: 680,
          margin: "0 auto",
          padding: "8rem 5vw",
          textAlign: "center",
        }}
      >
        <div className="section-label" style={{ justifyContent: "center" }}>
          <span>Contact</span>
        </div>
        <h2 className="section-title">Let's talk</h2>
        <p className="section-sub" style={{ maxWidth: "44ch", margin: "0 auto 2.5rem" }}>
          Whether you're hiring, building something for developers or technical buyers, or just want to compare
          notes on product marketing, I read every email.
        </p>
        <a
          href="mailto:robertopescee@gmail.com"
          style={{
            display: "block",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "1.8rem",
            fontStyle: "italic",
            color: "var(--gold-lt)",
            textDecoration: "none",
            marginBottom: "2.8rem",
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--gold)")}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--gold-lt)")}
        >
          robertopescee@gmail.com
        </a>
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <a href="mailto:robertopescee@gmail.com" className="btn-primary">
            Send an Email
          </a>
          <a
            href="https://linkedin.com/in/robertopescejr"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            LinkedIn
          </a>
        </div>
      </div>
    </section>

    {/* Responsive overrides */}
    <style>{`
      @media (max-width: 960px) {
        .story-grid { grid-template-columns: 1fr !important; }
        .about-grid { grid-template-columns: 1fr !important; gap: 3rem !important; }
        .about-grid > div:first-child { max-width: 300px; margin: 0 auto; }
        .testimonials-responsive { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </Layout>
);

export default Index;
